import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import Layout from './components/common/Layout';
import PrivateRoute from './components/common/PrivateRoute';
import AdminRoute from './components/common/AdminRoute';
import { ToastContainer, useToast } from './components/ui';

// Pages
import Landing from './pages/Landing/Landing';  
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import VerifyPhone from './pages/VerifyPhone/VerifyPhone';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';       
import ResetPassword from './pages/ResetPassword/ResetPassword';           
import RegistrationPaymentPage from './pages/RegistrationPayment/RegistrationPaymentPage';
import Dashboard from './pages/Dashboard/Dashboard';
import Templates from './pages/Templates/Templates';
import AiConfig from './pages/AiConfig/AiConfig';
import Phonebooks from './pages/Phonebooks/Phonebooks';
import SendCampaign from './pages/SendCampaign/SendCampaign';
import Products from './pages/Products/Products';
import Onboarding from './pages/Onboarding/Onboarding';
import OnboardingCallback from './pages/Onboarding/OnboardingCallback';
import Inbox from './pages/Inbox/Inbox';
import Reports from './pages/Reports/Reports';
import Billing from './pages/Billing/Billing';
import EscalationPage from './pages/Escalation/EscalationPage';
import ReminderSettings from './pages/ReminderSettings/ReminderSettings'; 
import Notifications from './pages/Notifications/Notifications';

// Admin Pages
import AdminOrganizations from './pages/Admin/AdminOrganizations';
import AdminUsers from './pages/Admin/AdminUsers';
import AdminCredits from './pages/Admin/AdminCredits';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminPayments from './pages/Admin/AdminPayments';
import AdminPricing from './pages/Admin/AdminPricing';

const ToastContainerWrapper = () => {
  const { toasts, removeToast } = useToast();
  return <ToastContainer toasts={toasts} onRemove={removeToast} />;
};

// ── Wrapper component with WebSocket ──────────────────────
const AppWithWebSocket = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  // Only provide WebSocket if user is authenticated
  if (user) {
    return (
      <WebSocketProvider>
        <AppRoutes />
      </WebSocketProvider>
    );
  }
  return <AppRoutes />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-phone" element={<VerifyPhone />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />      
      <Route path="/reset-password" element={<ResetPassword />} />   
      <Route path="/registration-payment" element={<RegistrationPaymentPage />} />

      {/* Protected Routes with Layout */}
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="templates" element={<Templates />} />
        <Route path="ai-config" element={<AiConfig />} />
        <Route path="phonebooks" element={<Phonebooks />} />
        <Route path="send" element={<SendCampaign />} />
        <Route path="products" element={<Products />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="onboarding/callback" element={<OnboardingCallback />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="reports" element={<Reports />} />
        <Route path="billing" element={<Billing />} />
        <Route path="escalation" element={<EscalationPage />} />
        <Route path="reminder-settings" element={<ReminderSettings />} />
        <Route path="notifications" element={<Notifications />} />

        {/* SUPER_ADMIN Routes */}
        <Route path="admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="admin/organizations" element={<AdminRoute><AdminOrganizations /></AdminRoute>} />
        <Route path="admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
        <Route path="admin/credits" element={<AdminRoute><AdminCredits /></AdminRoute>} />
        <Route path="admin/payments" element={<AdminRoute><AdminPayments /></AdminRoute>} />
        <Route path="admin/pricing" element={<AdminRoute><AdminPricing /></AdminRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppWithWebSocket />
        <ToastContainerWrapper />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
